import React, { useEffect, useRef, useState } from 'react';
import { FlipbaseConfig, PlayerInstance } from '../types/flipbase';

interface FlipbasePlayerProps {
  config: FlipbaseConfig;
}

export const FlipbasePlayer: React.FC<FlipbasePlayerProps> = ({ config }) => {
  const videoPlayerRef = useRef<PlayerInstance | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerId = 'flipbase-player-container';
  const selector = containerId; // Library expects ID without '#' prefix
  const { videoId, playerId, signature } = config;
  const [scriptReady, setScriptReady] = useState(false);
  const [containerReady, setContainerReady] = useState(false);

  // Load script
  useEffect(() => {
    const loadScript = () => {
      return new Promise<void>((resolve, reject) => {
        const existingScript = document.querySelector('script[src*="player.min.js"]');
        if (existingScript) {
          // Script already exists, just wait for it to load
          if (window.FlipbasePlayer) {
            resolve();
          } else {
            existingScript.addEventListener('load', () => resolve());
            existingScript.addEventListener('error', reject);
          }
          return;
        }

        const script = document.createElement('script');
        script.src = process.env.REACT_APP_PLAYER_URL || 'https://cdn.flipbase.com/player/v2/player.min.js';
        script.async = true;
        script.setAttribute('data-flipbase-player', 'true');
        script.onload = () => {
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    loadScript().then(() => {
      const checkInterval = setInterval(() => {
        if (window.FlipbasePlayer) {
          setScriptReady(true);
          clearInterval(checkInterval);
        }
      }, 100);

      setTimeout(() => clearInterval(checkInterval), 10000);
    }).catch(error => {
      console.error('Error loading Flipbase Player script:', error);
    });

    return () => {
      if (videoPlayerRef.current) {
        try {
          videoPlayerRef.current.unmount();
        } catch (e) {
          console.error('Error unmounting player:', e);
        }
      }
    };
  }, []);

  // Check container
  useEffect(() => {
    if (containerRef.current) {
      setContainerReady(true);
    }
  }, []);

  // Initialize player
  useEffect(() => {
    if (!scriptReady || !containerReady || !videoId || !playerId) {
      return;
    }

    const initializePlayer = () => {
      try {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.unmount();
        }

        // Make sure container is empty
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        // Basic required configuration
        const playerConfig: any = {
          video_id: videoId,
          player_id: playerId,
          selector,
          // Using fixed dimensions to avoid responsive calculations
          width: 640,
          height: 360,
          controls: true,
          options: {
            autoplay: false,
            responsive: false,
            enableProgressbar: true,
            defaultControls: true
          }
        };

        if (signature) {
          playerConfig.signature = signature;
        }

        console.log("Initializing player with config:", playerConfig);
        
        // Wait for next frame to ensure DOM is ready
        requestAnimationFrame(() => {
          videoPlayerRef.current = new window.FlipbasePlayer(playerConfig);
          
          // Add event listeners for progress
          videoPlayerRef.current.on('ready', () => {
            console.log('Player ready');
          });
          
          videoPlayerRef.current.on('timeupdate', (event) => {
            console.log('Time update:', event);
          });
          
          videoPlayerRef.current.on('seeking', (event) => {
            console.log('Seeking:', event);
          });
          
          videoPlayerRef.current.on('seeked', (event) => {
            console.log('Seeked:', event);
          });
          
          videoPlayerRef.current.on('error', (error) => {
            console.error('Player error:', error);
          });
          
          videoPlayerRef.current.mount();
        });
      } catch (error) {
        console.error('Error initializing Flipbase Player:', error);
      }
    };

    // Small delay to ensure everything is ready
    const timeoutId = setTimeout(initializePlayer, 100);

    return () => {
      clearTimeout(timeoutId);
      if (videoPlayerRef.current) {
        try {
          videoPlayerRef.current.unmount();
        } catch (e) {
          console.error('Error unmounting player:', e);
        }
      }
    };
  }, [videoId, playerId, signature, scriptReady, containerReady]);

  return (
    <div
      ref={containerRef}
      id={'flipbase-player-container'}
      data-testid="flipbase-player-container"
      style={{
        background: '#0b0c1013',
        borderRadius: '14px',
        overflow: 'hidden'
      }}
    />
  );
};

